import { gql } from '@apollo/client'
import { Button } from '@farol-ds/react'
import { BoxPaywall } from '@jusbrasil-web/shared-box-lock'
import { BoxUniqueFeatureModalProduct } from '@jusbrasil-web/shared-feature-locker'
import { ReactNode, useEffect } from 'react'

import { useMetrics } from '../../hooks'
import {
  ViewLockWrapper_DocumentFragment,
  ViewLockWrapper_ViewerFragment,
} from './__generated__/view-lock-wrapper.graphql'
import { LockType } from './constants'
import { LockWall } from './lock-wall'
import { LoginWall } from './login-wall'
import { ValidateOabTrigger } from './validate-oab-trigger'
import { useViewLockContext } from './view-lock-context'
import { ViewLockToast } from './view-lock-toast/view-lock-toast'
import styles from './view-lock-wrapper.module.scss'

export interface ViewLockWrapperProps {
  viewer: ViewLockWrapper_ViewerFragment | null
  document: ViewLockWrapper_DocumentFragment
  eventData: {
    from_component: string
    app_id: string
    metadata?: Record<string, unknown>
    [key: string]: unknown
  }
  children: ReactNode
  maxHeight?: string
  loginWallProps?: {
    title: string
    subtitle: string
  }
  lawyerLockProps?: {
    title: string
    subtitle?: string
    buttonText: string
  }
  oabLockProps?: {
    title: string
    subtitle?: string
    buttonText: string
  }
  isGooglebotAgent?: boolean
  loginWall?: ReactNode
  product?: BoxUniqueFeatureModalProduct
}

export function ViewLockWrapper(props: ViewLockWrapperProps) {
  const {
    viewer,
    document,
    children,
    maxHeight = '300px',
    eventData,
    loginWallProps = {
      title: 'Você atingiu o limite de utilização para usuários não cadastrados',
      subtitle: 'Para continuar utilizando:',
    },
    lawyerLockProps = {
      title: 'O acesso a esse documento é limitado a profissionais advogados',
      subtitle: 'Para continuar',
      buttonText: 'Atualizar profissão',
    },
    oabLockProps = {
      title: 'O acesso a esse documento é limitado a profissionais advogados com OAB verificada',
      subtitle: 'Para continuar',
      buttonText: 'Verificar OAB',
    },
    isGooglebotAgent = false,
    loginWall,
  } = props

  const { isLocked, lockType, isFreeUsage, featureCode, hasPaidCampaign } = useViewLockContext()
  const { sendMetrics, ref: metricsRef } = useMetrics<HTMLDivElement>()

  const { boxResourceId } = document
  const { app_id: appId, from_component: fromComponent } = eventData

  const showPaywall = isLocked && !isGooglebotAgent
  const showToast = isFreeUsage && !showPaywall && !!viewer && !viewer.boxProfileInfo?.linkedAccount

  const boxLockProps = {
    appId,
    featureCode,
    fromComponent,
    boxOptions: {
      resource_id: boxResourceId,
    },
    pageMetadata: {},
  }
  useEffect(() => {
    if (!isFreeUsage) {
      return
    }
    const eventName = isLocked ? 'ViewLockViewed' : 'ViewWarningViewed'
    sendMetrics(`Doc.${eventName}`, {
      ...eventData,
      metadata: {
        ...eventData.metadata,
        lockType,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocked, isFreeUsage])

  const handleLoginWallProps = () => {
    const ppcUsersProps = {
      ...loginWallProps,
      title: 'Conteúdo exclusivo para assinantes.',
      subtitle: 'Para assinar e continuar utilizando:',
    }
    return hasPaidCampaign ? ppcUsersProps : loginWallProps
  }

  return (
    <>
      {showPaywall ? (
        <>
          <div data-testid="lock-wrapper" className={styles.viewLockWrapper} style={{ maxHeight }}>
            {children}
          </div>

          {lockType === LockType.UNLOGGED &&
            (loginWall || (
              <LoginWall
                viewer={viewer}
                loginWarningProps={handleLoginWallProps()}
                eventData={{ ...eventData, feature_code: featureCode }}
              />
            ))}
          {LockType.LOGGED === lockType && (
            <div>
              <BoxPaywall {...boxLockProps} viewer={viewer} />
            </div>
          )}
          {lockType === LockType.LAWYER && viewer && (
            <LockWall {...lawyerLockProps}>
              <Button
                className={styles.lockWallButton}
                kind="primary"
                onClick={() => {
                  if (viewer.editUrl) {
                    window.location.href = viewer.editUrl
                  }
                }}
              >
                {lawyerLockProps.buttonText}
              </Button>
            </LockWall>
          )}
          {lockType === LockType.OAB && viewer && <ValidateOabTrigger {...oabLockProps} />}
        </>
      ) : (
        <>{children}</>
      )}

      {showToast && <ViewLockToast />}

      <span ref={metricsRef} />
    </>
  )
}

ViewLockWrapper.fragments = {
  viewer: gql`
    fragment ViewLockWrapper_viewer on Profile {
      editUrl
      ...BoxPaywall_viewer
      boxProfileInfo {
        linkedAccount
      }
    }
    ${BoxPaywall.fragments.viewer}
  `,

  document: gql`
    fragment ViewLockWrapper_document on Document {
      boxResourceId
    }
  `,
}
